/*
 * UNIK product card gallery (F3-03), port of the Shella reference's card engine
 * (assets/unik-product-card.js) to a custom element in UNIK's product card.
 *
 * <unik-card-gallery data-images='[{id, src, alt}, …]'> sits in the card's media and swaps
 * the card's own image (`.unik-product-card__media img`). Kept from the reference:
 *  - an image is decoded before the swap; the latest request wins;
 *  - a slide animation, skipped with prefers-reduced-motion;
 *  - a 500 ms click suppression after a swipe, so a swipe never opens the product.
 * Changed: neighbour images load on the first pointerenter/focusin/touchstart on the card
 * (no IntersectionObserver prefetch), widths are clamp(320, rendered width × DPR, 1600),
 * and there is no global MutationObserver (custom element lifecycle instead).
 *
 * Swipe (2026-10-25, docs/changes/2026-10-25-card-gallery-swipe.md), as in Swiper and Embla:
 * touch events, because only a non-passive touchmove can stop the page from scrolling
 * (preventDefault on pointermove cannot), and Safari lets a small vertical drift take over
 * a touch-action: pan-y gesture (WebKit bug 232545). The direction is decided once, after
 * 6 px, at 45°; a horizontal gesture then owns every move. The image follows the finger
 * with the next one beside it, and a flick (< 300 ms) or a quarter of the width changes it.
 */
(() => {
  if (customElements.get('unik-card-gallery')) return;

  const MAX_WIDTH = 1600;
  // The image frame of UNIK's product card (snippets/unik-product-card.liquid).
  const MEDIA = '.unik-product-card__media';
  const LOCK_DISTANCE = 6;
  const FLICK_MS = 300;
  const FLICK_DISTANCE = 12;
  const EASING = 'cubic-bezier(.2,.7,.3,1)';
  const imageLoads = new Map();
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  function loadImage(src) {
    if (imageLoads.has(src)) return imageLoads.get(src);
    const promise = new Promise((resolve, reject) => {
      const preload = new Image();
      preload.onload = async () => {
        // Decode before swapping so the animation never starts with an empty frame.
        if (preload.decode) await preload.decode().catch(() => {});
        resolve(src);
      };
      preload.onerror = () => {
        imageLoads.delete(src);
        reject(new Error('Product image could not load'));
      };
      preload.src = src;
    });
    imageLoads.set(src, promise);
    return promise;
  }

  /** A copy of the image above it in the frame, for the slide and the swipe. */
  function overlay(image) {
    const frame = image.parentElement;
    const box = image.getBoundingClientRect();
    const frameBox = frame.getBoundingClientRect();
    const copy = image.cloneNode();
    copy.removeAttribute('id');
    copy.removeAttribute('loading');
    copy.dataset.unikSlideClone = '';
    copy.alt = '';
    copy.setAttribute('aria-hidden', 'true');
    Object.assign(copy.style, {
      position: 'absolute',
      left: `${box.left - frameBox.left}px`,
      top: `${box.top - frameBox.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      zIndex: '1',
      pointerEvents: 'none',
      transform: '',
    });
    frame.appendChild(copy);
    return { copy, width: box.width };
  }

  class UnikCardGallery extends HTMLElement {
    connectedCallback() {
      try {
        this.images = JSON.parse(this.dataset.images || '[]').slice(0, 3);
      } catch {
        this.images = [];
      }
      this.card = this.closest('.unik-product-card') || this.parentElement;
      if (this.images.length < 2 || !this.card || this.handlers) return;

      this.request = 0;
      this.current = 0;
      this.target = null;
      this.finishSlide = null;
      this.suppressClicksUntil = 0;
      this.gesture = null;
      this.dragState = null;
      this.prefetched = false;

      this.handlers = {
        click: this.onClick.bind(this),
        prefetch: () => this.prefetch(),
        cardClick: this.onCardClick.bind(this),
        touchstart: this.onTouchStart.bind(this),
        touchmove: this.onTouchMove.bind(this),
        touchend: this.onTouchEnd.bind(this),
        touchcancel: () => this.cancelGesture(),
      };
      this.addEventListener('click', this.handlers.click);
      for (const type of ['pointerenter', 'focusin', 'touchstart']) {
        this.card.addEventListener(type, this.handlers.prefetch, { passive: true });
      }
      this.card.addEventListener('click', this.handlers.cardClick, true);
      this.card.addEventListener('touchstart', this.handlers.touchstart, { passive: true });
      // Not passive: preventDefault keeps a horizontal swipe from scrolling the page.
      this.card.addEventListener('touchmove', this.handlers.touchmove, { passive: false });
      this.card.addEventListener('touchend', this.handlers.touchend);
      this.card.addEventListener('touchcancel', this.handlers.touchcancel);
    }

    disconnectedCallback() {
      if (!this.handlers || !this.card) return;
      this.removeEventListener('click', this.handlers.click);
      for (const type of ['pointerenter', 'focusin', 'touchstart']) {
        this.card.removeEventListener(type, this.handlers.prefetch);
      }
      this.card.removeEventListener('click', this.handlers.cardClick, true);
      this.card.removeEventListener('touchstart', this.handlers.touchstart);
      this.card.removeEventListener('touchmove', this.handlers.touchmove);
      this.card.removeEventListener('touchend', this.handlers.touchend);
      this.card.removeEventListener('touchcancel', this.handlers.touchcancel);
      this.handlers = null;
      this.gesture = null;
      this.releaseDrag();
      this.finishSlide?.();
    }

    get image() {
      return this.card.querySelector(`${MEDIA} img:not([data-unik-slide-clone])`);
    }

    get media() {
      return this.card.querySelector(MEDIA);
    }

    sourceFor(item) {
      const width = this.image?.getBoundingClientRect().width || 0;
      const pixels = Math.ceil((width * (window.devicePixelRatio || 1)) / 100) * 100;
      const url = new URL(item.src, window.location.href);
      url.searchParams.set('width', String(Math.min(MAX_WIDTH, Math.max(320, pixels))));
      return url.toString();
    }

    neighbour(direction) {
      const count = this.images.length;
      return ((this.target ?? this.current) + direction + count) % count;
    }

    /** Loads the neighbouring images once the customer starts interacting with the card. */
    prefetch() {
      if (this.prefetched) return;
      this.prefetched = true;
      for (const direction of [-1, 1]) {
        loadImage(this.sourceFor(this.images[this.neighbour(direction)])).catch(() => {});
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-direction], [data-index]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      if (button.hasAttribute('data-index')) this.show(Number(button.dataset.index));
      else this.step(Number(button.dataset.direction) || 1);
    }

    step(direction) {
      this.show(this.neighbour(direction), direction);
    }

    async show(index, direction) {
      const from = this.target ?? this.current;
      if (index < 0 || index >= this.images.length || index === from) return;
      direction ??= index > from ? 1 : -1;
      this.target = index;
      const request = ++this.request;
      const next = this.images[index];
      this.setAttribute('aria-busy', 'true');
      try {
        const src = await loadImage(this.sourceFor(next));
        if (request !== this.request || !this.isConnected) return;
        this.slide(src, next, direction);
        this.current = index;
        this.querySelectorAll('[data-index]').forEach((dot) => {
          dot.setAttribute('aria-pressed', String(Number(dot.dataset.index) === index));
        });
        this.status(this.messageFor('Image', index));
        this.prefetched = false;
        this.prefetch();
      } catch {
        if (request === this.request) {
          this.snapBack();
          this.status(this.statusElement?.dataset.messageError || '');
        }
      } finally {
        if (request === this.request) {
          this.target = null;
          this.removeAttribute('aria-busy');
        }
      }
    }

    get statusElement() {
      return this.querySelector('[data-status]');
    }

    messageFor(kind, index) {
      return (this.statusElement?.dataset[`message${kind}`] || '').replace('__index__', String(index + 1));
    }

    status(text) {
      if (this.statusElement) this.statusElement.textContent = text;
    }

    /** Swaps the image with a slide; after a swipe it continues from where the finger let go. */
    slide(src, next, direction) {
      this.finishSlide?.();
      const offset = this.dragState?.dx || 0;
      this.releaseDrag();
      const image = this.image;
      if (!image) return;
      const { copy: outgoing, width } = overlay(image);

      image.removeAttribute('sizes');
      image.setAttribute('srcset', src);
      image.src = src;
      image.alt = next.alt || '';
      image.dataset.imageId = String(next.id);

      if (reducedMotion?.matches || !image.animate) {
        outgoing.remove();
        return;
      }
      const remaining = width ? 1 - Math.min(1, Math.abs(offset) / width) : 1;
      const options = { duration: Math.max(120, 220 * remaining), easing: EASING };
      const incoming = image.animate([{ transform: `translateX(${direction * width + offset}px)` }, { transform: 'translateX(0)' }], options);
      const leaving = outgoing.animate([{ transform: `translateX(${offset}px)` }, { transform: `translateX(${-direction * width}px)` }], options);
      const finish = () => {
        incoming.cancel();
        leaving.cancel();
        outgoing.remove();
        if (this.finishSlide === finish) this.finishSlide = null;
      };
      this.finishSlide = finish;
      Promise.allSettled([incoming.finished, leaving.finished]).then(finish);
    }

    // --- Swipe (touch) -------------------------------------------------------------------

    inMedia(point) {
      const box = this.media?.getBoundingClientRect();
      if (!box) return false;
      return point.clientX >= box.left && point.clientX <= box.right && point.clientY >= box.top && point.clientY <= box.bottom;
    }

    touchOf(event) {
      return Array.from(event.changedTouches || []).find((touch) => touch.identifier === this.gesture?.id);
    }

    onTouchStart(event) {
      if (event.touches.length > 1) return this.cancelGesture();
      const touch = event.changedTouches[0];
      if (!touch || event.target.closest?.('button') || !this.inMedia(touch)) return;
      if (this.closest('[data-unik-card-swipe="off"]') && window.matchMedia('(max-width: 989px)').matches) return;
      this.gesture = { id: touch.identifier, x: touch.clientX, y: touch.clientY, time: Date.now(), axis: null };
      this.prefetch();
    }

    onTouchMove(event) {
      const gesture = this.gesture;
      if (!gesture) return;
      // A second finger is a pinch zoom: leave it to the browser.
      if (event.touches.length > 1) return this.cancelGesture();
      const touch = this.touchOf(event);
      if (!touch) return;
      const dx = touch.clientX - gesture.x;
      const dy = touch.clientY - gesture.y;
      if (!gesture.axis) {
        if (Math.hypot(dx, dy) < LOCK_DISTANCE) return;
        // A move that cannot be cancelled means the browser already scrolls.
        gesture.axis = event.cancelable && Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (gesture.axis === 'y') {
          this.gesture = null;
          return;
        }
        this.startDrag();
      }
      if (event.cancelable) event.preventDefault();
      this.suppressClicksUntil = Date.now() + 500;
      this.drag(dx);
    }

    onTouchEnd(event) {
      const gesture = this.gesture;
      if (!gesture || !this.touchOf(event)) return;
      this.gesture = null;
      if (gesture.axis !== 'x') return;
      this.suppressClicksUntil = Date.now() + 500;
      const dx = this.dragState?.dx || 0;
      const width = this.dragState?.width || this.media?.clientWidth || 0;
      const flick = Date.now() - gesture.time < FLICK_MS && Math.abs(dx) >= FLICK_DISTANCE;
      if (flick || Math.abs(dx) >= Math.min(60, width * 0.25)) this.step(dx < 0 ? 1 : -1);
      else this.snapBack();
    }

    cancelGesture() {
      const dragging = this.gesture?.axis === 'x';
      this.gesture = null;
      if (dragging) this.snapBack();
    }

    /** The image follows the finger, with the neighbour on the side it comes in from. */
    startDrag() {
      this.finishSlide?.();
      this.releaseDrag();
      const image = this.image;
      if (!image) return;
      const { copy: peek, width } = overlay(image);
      peek.removeAttribute('srcset');
      peek.removeAttribute('sizes');
      peek.style.visibility = 'hidden';
      this.dragState = { image, peek, width, direction: 0, dx: 0 };
    }

    drag(dx) {
      const state = this.dragState;
      if (!state) return;
      const direction = dx < 0 ? 1 : -1;
      if (direction !== state.direction) {
        state.direction = direction;
        state.peek.src = this.sourceFor(this.images[this.neighbour(direction)]);
        state.peek.style.visibility = '';
      }
      state.dx = dx;
      state.image.style.transform = `translateX(${dx}px)`;
      state.peek.style.transform = `translateX(${direction * state.width + dx}px)`;
    }

    /** Back to the current image when the swipe was too short (or the next image failed). */
    snapBack() {
      const state = this.dragState;
      if (!state) return;
      if (reducedMotion?.matches || !state.image.animate || !state.dx) return this.releaseDrag();
      const { image, peek, width, direction, dx } = state;
      image.style.transform = '';
      peek.style.transform = `translateX(${direction * width}px)`;
      const options = { duration: 180, easing: EASING };
      const animations = [
        image.animate([{ transform: `translateX(${dx}px)` }, { transform: 'translateX(0)' }], options),
        peek.animate([{ transform: `translateX(${direction * width + dx}px)` }, { transform: `translateX(${direction * width}px)` }], options),
      ];
      state.dx = 0;
      Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
        if (this.dragState === state) this.releaseDrag();
      });
    }

    releaseDrag() {
      const state = this.dragState;
      if (!state) return;
      this.dragState = null;
      state.peek.remove();
      state.image.style.transform = '';
    }

    /** A swipe ends with a click on the card link; it must not open the product. */
    onCardClick(event) {
      // event.detail is 0 for keyboard activation, which is never a swipe.
      if (event.detail === 0 || Date.now() >= this.suppressClicksUntil || this.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  customElements.define('unik-card-gallery', UnikCardGallery);
})();
